import { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'state'
import { addressesEqual } from 'utils/addressHelpers'
import { useWagmi } from 'libs/wagmi'
import useAuth from 'hooks/useAuth'
import { FetchStatus, State } from 'state/types'
import { fetchAccountInfo, fetchSession, profileClear } from '.'

export const useIsAuthenticated = () => {
  const { authentication, sessionFetchStatus } = useSelector((state: State) => state.profile)
  const { account } = useWagmi()

  return useMemo(
    () =>
      sessionFetchStatus === FetchStatus.FETCHED &&
      !!authentication &&
      !!account &&
      addressesEqual(authentication.address, account),
    [account, authentication, sessionFetchStatus],
  )
}

export const useFetchProfile = () => {
  const { account } = useWagmi()
  const dispatch = useAppDispatch()
  const { authentication, sessionFetchStatus } = useSelector((state: State) => state.profile)
  const { linkWallet } = useAuth()
  const isAuthenticated = useIsAuthenticated()

  // Track which accounts we've already attempted to link to prevent infinite loops
  const linkingAttempts = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (account) {
      dispatch(profileClear())
      dispatch(fetchSession())
      // Clear linking attempts when account changes
      linkingAttempts.current.clear()
    }
  }, [account, dispatch])

  useEffect(() => {
    if (sessionFetchStatus === FetchStatus.FETCHED && account && authentication) {
      const isNewAccount = authentication.address.toLowerCase() !== account.toLowerCase()
      const attemptKey = `${account.toLowerCase()}_${authentication.address.toLowerCase()}`

      // Only call linkWallet if it's a new account and we haven't attempted it yet
      if (isNewAccount && !linkingAttempts.current.has(attemptKey)) {
        linkingAttempts.current.add(attemptKey)

        linkWallet().catch((error) => {
          console.error('Failed to link wallet:', error)
          // If user denies signature, clear the session to stop retrying
          if (error?.code === 4001 || error?.code === 'ACTION_REJECTED') {
            dispatch(profileClear())
          }
        })
      }
    }
  }, [sessionFetchStatus, account, authentication, dispatch, linkWallet])

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchAccountInfo())
    } else {
      dispatch(profileClear())
    }
  }, [isAuthenticated, dispatch])
}
